import {
  PlaceholderSeasonSkipCode,
  PlaceholderSelectionPreview,
  phishingSignsLabels,
} from '@/client/campaigns';
import { LoadingStatuses } from '@/common/constants';
import i18n from '@/i18n';
import { DateFormats } from '@/system-settings/enums';
import { AppButton } from '@/ui/buttons';
import { TFunction } from 'i18next';
import moment from 'moment';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const PanelContainer = styled.div`
  display: grid;
  row-gap: var(--xsmall-padding);
  min-width: 260px;
  max-width: 320px;
  text-align: left;
  white-space: normal;
  line-height: var(--xsmall-line-height);
`;

const PanelTitle = styled.div`
  font-weight: 600;
  padding-bottom: var(--xsmall-padding);
  border-bottom: 1px solid var(--purple-highlight);
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  column-gap: var(--medium-padding);
  row-gap: var(--xxsmall-padding);
`;

const MutedText = styled.span`
  color: var(--purple-light);
`;

const ReasonText = styled(MutedText)`
  padding-left: var(--small-padding);
`;

const PickExplanation = styled(MutedText)`
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  overflow: hidden;
`;

const ErrorText = styled.div`
  color: var(--red-light);
`;

const CandidateSection = styled.div`
  display: grid;
  row-gap: var(--xxsmall-padding);
`;

const CandidateList = styled.ul`
  display: grid;
  row-gap: var(--xxsmall-padding);
  list-style: none;
  margin: 0;
  padding: 0;
`;

const CandidateRow = styled.div`
  display: flex;
  justify-content: space-between;
  column-gap: var(--medium-padding);
`;

const CandidateName = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CandidateMarker = styled(MutedText)`
  flex-shrink: 0;
`;

const PickAction = styled.div`
  display: flex;
  align-items: center;
  column-gap: var(--small-padding);
  flex-wrap: wrap;
  row-gap: var(--xxsmall-padding);
`;

const PickButton = styled(AppButton)`
  &.p-button {
    height: auto;
    padding: var(--xxsmall-padding) var(--small-padding);
    font-size: var(--xsmall-font-size);
    line-height: var(--xsmall-line-height);
  }
`;

const LANGUAGE_SUFFIX_PATTERN = / - [A-Z]{2,3}$/;

const SEASON_SKIP_KEYS: Record<PlaceholderSeasonSkipCode, string> = {
  'no-window': 'phishing.autoSchedule.preview.season.skip.noWindow',
  'no-template': 'phishing.autoSchedule.preview.season.skip.noTemplate',
  'claimed-elsewhere':
    'phishing.autoSchedule.preview.season.skip.claimedElsewhere',
};

const UNNAMED_SEASON_SKIP_KEYS: Record<PlaceholderSeasonSkipCode, string> = {
  'no-window': 'phishing.autoSchedule.preview.season.skip.noWindow',
  'no-template': 'phishing.autoSchedule.preview.season.skip.noTemplateUnnamed',
  'claimed-elsewhere':
    'phishing.autoSchedule.preview.season.skip.claimedElsewhereUnnamed',
};

const stripLanguageSuffix = (name: string) =>
  name.replace(LANGUAGE_SUFFIX_PATTERN, '');

const resolveSignReason = (
  t: TFunction,
  preview: PlaceholderSelectionPreview,
) => {
  const { signReasonCode, signRank, seasonName } = preview;

  if (signReasonCode === 'least-used') {
    return t('phishing.autoSchedule.preview.sign.reason.leastUsed');
  }

  if (signReasonCode === 'nth-least-used' && !!signRank) {
    return t('phishing.autoSchedule.preview.sign.reason.nthLeastUsed', {
      count: signRank,
      ordinal: true,
    });
  }

  if (signReasonCode === 'season' && !!seasonName) {
    return t('phishing.autoSchedule.preview.sign.reason.season', {
      season: seasonName,
    });
  }

  return null;
};

const resolveSeasonReason = (
  t: TFunction,
  preview: PlaceholderSelectionPreview,
) => {
  const { seasonSkipCode, seasonSkipName } = preview;

  if (!seasonSkipCode) {
    return null;
  }

  const reasonKey = seasonSkipName
    ? SEASON_SKIP_KEYS[seasonSkipCode]
    : UNNAMED_SEASON_SKIP_KEYS[seasonSkipCode];

  return reasonKey ? t(reasonKey, { season: seasonSkipName }) : null;
};

export type PlaceholderPreviewPanelProps = {
  preview?: PlaceholderSelectionPreview;
  hasLoadError?: boolean;
  likelyPickName?: string;
  likelyPickExplanation?: string | null;
  isGeneratingPick?: boolean;
  hasPickError?: boolean;
  onGeneratePick: () => void;
};

export const PlaceholderPreviewPanel: React.FC<
  PlaceholderPreviewPanelProps
> = ({
  preview,
  hasLoadError,
  likelyPickName,
  likelyPickExplanation,
  isGeneratingPick,
  hasPickError,
  onGeneratePick,
}) => {
  const { t } = useTranslation();

  if (hasLoadError) {
    return (
      <ErrorText>{t('phishing.autoSchedule.preview.error.load')}</ErrorText>
    );
  }

  if (!preview) {
    return null;
  }

  const hasCandidates = preview.candidates.length > 0;
  const explanation = likelyPickExplanation?.trim();
  const signLabels = phishingSignsLabels(t);
  const signReason = resolveSignReason(t, preview);
  const seasonReason = resolveSeasonReason(t, preview);
  const excludedCount = preview.excludedBenchmarkCount ?? 0;

  return (
    <PanelContainer>
      <PanelTitle>
        {t('phishing.autoSchedule.preview.title', {
          date: moment(preview.launchDate)
            .locale(i18n.language)
            .format(DateFormats.DATEONLY),
        })}
      </PanelTitle>

      <DetailGrid>
        <MutedText>{t('phishing.autoSchedule.preview.sign.label')}</MutedText>
        <span>
          {signLabels[preview.sign]}
          {!!signReason && <ReasonText>{signReason}</ReasonText>}
        </span>
        <MutedText>{t('phishing.autoSchedule.preview.season.label')}</MutedText>
        <span>
          {preview.seasonName ?? t('phishing.autoSchedule.preview.season.none')}
          {!!seasonReason && <ReasonText>{seasonReason}</ReasonText>}
        </span>
      </DetailGrid>

      {hasCandidates ? (
        <CandidateSection>
          <CandidateRow>
            <MutedText>
              {t('phishing.autoSchedule.preview.candidates.count', {
                count: preview.candidates.length,
              })}
            </MutedText>
            {excludedCount > 0 && (
              <MutedText>
                {t('phishing.autoSchedule.preview.candidates.excluded', {
                  count: excludedCount,
                })}
              </MutedText>
            )}
          </CandidateRow>
          <CandidateList>
            {preview.candidates.map((candidate) => (
              <CandidateRow as="li" key={candidate.id}>
                <CandidateName title={candidate.name}>
                  {stripLanguageSuffix(candidate.name)}
                </CandidateName>
                {candidate.id === preview.fallbackCandidateId && (
                  <CandidateMarker
                    title={t(
                      'phishing.autoSchedule.preview.candidates.fallbackTitle',
                    )}
                  >
                    {t(
                      'phishing.autoSchedule.preview.candidates.fallbackLabel',
                    )}
                  </CandidateMarker>
                )}
              </CandidateRow>
            ))}
          </CandidateList>
        </CandidateSection>
      ) : (
        <MutedText>
          {t('phishing.autoSchedule.preview.candidates.empty')}
        </MutedText>
      )}

      {hasCandidates && !likelyPickName && (
        <PickAction>
          <PickButton
            label={t('phishing.autoSchedule.preview.aiPick.button')}
            severity="basic"
            state={
              isGeneratingPick ? LoadingStatuses.LOADING : LoadingStatuses.IDLE
            }
            onClick={onGeneratePick}
          />
          <MutedText>
            {t('phishing.autoSchedule.preview.aiPick.cost')}
          </MutedText>
        </PickAction>
      )}

      {likelyPickName && (
        <div>
          <div>
            {t('phishing.autoSchedule.preview.aiPick.result', {
              name: likelyPickName,
            })}
          </div>
          <MutedText>
            {t('phishing.autoSchedule.preview.aiPick.qualifier')}
          </MutedText>
          {!!explanation && (
            <PickExplanation title={explanation}>{explanation}</PickExplanation>
          )}
        </div>
      )}

      {hasPickError && (
        <ErrorText>{t('phishing.autoSchedule.preview.error.aiPick')}</ErrorText>
      )}
    </PanelContainer>
  );
};
